import { useState, useLayoutEffect } from 'react';
import { observeResizeAll } from '../utils/observe-resize.js';

const NON_EMPTY_TEXT_NODE = ':not(:empty)'; // note: inside React, whitespace won’t be rendered unless explicitly set
function computeInlinePadding(styles) {
    const inlinePadding = Number.parseFloat(styles.paddingInlineStart || styles.paddingLeft || '0') +
        Number.parseFloat(styles.paddingInlineEnd || styles.paddingRight || '0');
    return Number.isNaN(inlinePadding) ? 0 : Math.max(inlinePadding, 0);
}
/**
 * Callback hook that detects if any children within is truncated.
 * For performance reasons, it’s best to scope this as closely as possible
 * to the elements being measured (i.e. don’t wrap the entire app).
 */
function useDetectTruncated(ref, enabled = true) {
    const [isTruncated, setIsTruncated] = useState(false);
    // fire after initial render for safety
    useLayoutEffect(() => {
        const rootEl = ref.current;
        if (!rootEl || !enabled) {
            return;
        }
        const onResize = (entries) => {
            let isNextTruncated = false;
            for (const entry of entries) {
                if (entry.target.scrollWidth - computeInlinePadding(getComputedStyle(entry.target)) >
                    // Round entry.contentRect.width because entry.target.scrollWidth is rounded by default
                    Math.round(entry.contentRect.width)) {
                    isNextTruncated = true;
                    break;
                }
            }
            setIsTruncated(isNextTruncated);
        };
        // monitor resizes
        let disconnect = observeResizeAll(getTargets(rootEl), onResize);
        // monitor for child additions/removals
        const mo = new MutationObserver(() => {
            disconnect();
            disconnect = observeResizeAll(getTargets(rootEl), onResize);
        });
        mo.observe(rootEl, { childList: true });
        return () => {
            disconnect();
            mo.disconnect();
        };
    }, [ref, enabled]);
    return isTruncated;
}
function getTargets(el) {
    const childNodes = el.querySelectorAll(NON_EMPTY_TEXT_NODE);
    return childNodes.length ? childNodes : [el];
}

export { computeInlinePadding, useDetectTruncated };
//# sourceMappingURL=use-detect-truncated.js.map
